import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Bookmark, BookmarkCheck, Loader2, TrendingUp, DollarSign } from "lucide-react";
import { toast } from "sonner";
import PDFCTABanner from "../components/PDFCTABanner";
import JobsCTABanner from "../components/JobsCTABanner";

export default function Results() {
  const { quizId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [quizRes, savedRes] = await Promise.all([
          api.get(`/quiz/${quizId}`),
          api.get("/me/careers"),
        ]);
        setData(quizRes.data);
        setSavedIds(new Set(savedRes.data.items.map((i) => i.career.career_id)));
      } catch (e) {
        toast.error("Could not load results");
      } finally {
        setLoading(false);
      }
    })();
  }, [quizId]);

  const toggleSave = async (career) => {
    try {
      if (savedIds.has(career.career_id)) {
        await api.delete(`/me/careers/${career.career_id}`);
        const next = new Set(savedIds); next.delete(career.career_id); setSavedIds(next);
        toast.success("Removed from saved");
      } else {
        await api.post("/me/careers", { career, quiz_id: quizId });
        setSavedIds(new Set(savedIds).add(career.career_id));
        toast.success("Saved! Grab your full report below to keep everything.");
      }
    } catch {
      toast.error("Could not update saved careers");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="results-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="results-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-3">[ Results ] — Your matches</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
          Five careers built for who you are.
        </h1>
        <p className="mt-4 text-[#5C5C55] max-w-2xl">
          Ranked by fit. Open any one to see the formal degrees and informal
          courses we recommend to get you there.
        </p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.careers.map((c, idx) => (
            <article
              key={c.career_id}
              data-testid={`career-card-${idx}`}
              className="bg-white border border-[#E5E5E0] rounded-sm p-7 hover:border-[#002FA7]/40 hover:shadow-md transition-all fade-up flex flex-col"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <span className="font-mono text-xs text-[#5C5C55]">#{String(idx + 1).padStart(2, "0")}</span>
                <span
                  data-testid={`match-badge-${idx}`}
                  className="font-mono text-sm bg-[#002FA7]/10 text-[#002FA7] px-2.5 py-1 border border-[#002FA7]/20 rounded-sm"
                >
                  {c.match_percentage}% match
                </span>
              </div>

              <h3 className="font-heading text-2xl font-bold tracking-tight leading-tight mb-2">
                {c.title}
              </h3>
              <p className="text-[#5C5C55] text-sm leading-relaxed">{c.description}</p>

              <div className="mt-4 pt-4 border-t border-[#E5E5E0] space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <DollarSign className="w-4 h-4 text-[#5C5C55] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-[#0A0A0A]">{c.salary_range}</span>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-[#5C5C55] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-[#0A0A0A]">{c.growth_outlook}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#E5E5E0]">
                <div className="overline mb-2">Why it fits</div>
                <p className="text-sm text-[#0A0A0A] leading-relaxed">{c.why_match}</p>
              </div>

              <div className="mt-4 pt-4 border-t border-[#E5E5E0]">
                <div className="overline mb-2">Key skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {c.key_skills.map((s) => (
                    <span key={s} className="font-mono text-xs px-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-sm">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <Link
                  to={`/careers/${c.career_id}`}
                  data-testid={`view-career-${idx}`}
                  className="btn-primary flex-1 justify-center group"
                >
                  View path
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button
                  data-testid={`save-career-${idx}`}
                  onClick={() => toggleSave(c)}
                  className="p-3 border border-[#0A0A0A] rounded-sm hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  title={savedIds.has(c.career_id) ? "Saved" : "Save"}
                >
                  {savedIds.has(c.career_id) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* PDF CTA — right after user sees their 5 matches */}
        <div className="mt-12 space-y-4">
          <PDFCTABanner variant="compact" />
          <JobsCTABanner variant="info" educationLevel={data?.answers?.education_level} />
        </div>
      </div>
    </main>
  );
}
